import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { DashboardData, DashboardService } from 'src/app/services/dashboard.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

Chart.register(...registerables);

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  dashboard: DashboardData | null = null;
  loading = true;
  selectedTab = 0;
  private tabNames = ['concursos', 'inscricoes', 'comissao', 'provas'];
  private loadedTabs = new Set<string>();

  mesSelecionado: number;
  anoSelecionado: number;
  meses = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' },
  ];
  anos: number[] = [];
  periodoInicio: Date | null = null;
  periodoFim: Date | null = null;

  // Tooltips informativos
  infoTooltips = {
    andamento: 'Considera apenas concursos ativos no sistema.',
    unidade: 'Inclui todos os andamentos exceto encerrados.',
    fase: 'Apenas concursos com Lista Definitiva ou Divulgação da Comissão concluídos.',
    tipo: 'Apenas concursos ativos.',
    encerrados: 'Resultado Final Provisório ou Definitivo concluído.',
    inscricoesUnidade: 'Todas as inscrições, independente do status.',
    inscricoesPeriodo: 'Inclui inscrições em qualquer status (deferida, indeferida, não confirmada, etc.).',
    inscricoesAbertas: '⚠ = encerra em até 7 dias. Vermelho = nenhuma inscrição realizada.',
    naoConfirmadas: 'Apenas concursos cujo período de inscrição já encerrou.',
    comissao: 'Critério: 4 titulares + 1 suplente + 1 presidente. Exclui homologados/cancelados.',
    recursos: 'Verde = deferido, vermelho = indeferido, laranja = em análise.',
    membros: 'Exclui membros desligados.',
    provas: 'Total de provas cadastradas em concursos ativos.',
    generoUnidade: 'Todas as inscrições em concursos ativos, independente do status.',
    generoTipo: 'Todas as inscrições em concursos ativos.',
    heteroidentificacao: 'Apenas inscrições que possuem heteroidentificação registrada.',
    provasTipo: 'Provas agrupadas por tipo em cada fase.',
    ausencias: 'Candidatos marcados como não presentes na lista de presença.',
    classificacao: 'Apenas concursos com resultado de fase concluído.',
  } as const;

  isVisaoRestrita = false;

  // Charts - Concursos
  andamentoLabelsFull: string[] = [];
  andamentoHeight = 200;
  chartAndamento: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  chartConcursosPorUnidade: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  concursosPorUnidadeWidth = 400;
  chartEncerradosPorUnidade: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  encerradosPorUnidadeWidth = 400;
  chartConcursosPorTipo: ChartConfiguration<'pie'>['data'] = { labels: [], datasets: [] };
  chartConcursosPorFase: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  chartRecursos: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  recursosHeight = 200;

  // Charts - Inscrições
  chartInscricoesPorStatus: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  chartInscricoesPorUnidade: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  inscricoesPorUnidadeWidth = 400;
  chartNaoConfirmadas: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  naoConfirmadasHeight = 200;

  // Charts - Comissão
  chartMembros: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  membrosHeight = 200;
  membrosLabelsFull: { nome: string; matricula: string }[] = [];

  // Charts - Provas
  chartProvasPorFase: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };

  // Charts - Heteroidentificação
  chartHeteroidentificacao: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };

  // Charts - Provas por tipo e fase
  chartProvasPorTipoEFase: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  chartAusencias: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  chartClassificacaoPorFase: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };

  // Charts - Gênero (SG/Superadmin)
  chartGeneroPorUnidade: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  generoPorUnidadeWidth = 400;
  chartGeneroPorTipo: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };

  // Options
  barOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true, maintainAspectRatio: false,
    layout: { padding: { left: 8, right: 8, top: 8, bottom: 8 } },
    plugins: { tooltip: { enabled: true }, legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  horizontalBarOptions: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    layout: { padding: { left: 16, right: 24, top: 8, bottom: 8 } },
    plugins: {
      tooltip: { enabled: true, callbacks: { title: (items) => this.andamentoLabelsFull[items[0]?.dataIndex] || '' } },
      legend: { display: false },
    },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { ticks: { align: 'start', font: { size: 11 } } } },
  };

  membrosBarOptions: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    layout: { padding: { left: 16, right: 24, top: 8, bottom: 8 } },
    plugins: {
      tooltip: { enabled: true, callbacks: { title: (items) => {
        const m = this.membrosLabelsFull[items[0]?.dataIndex];
        return m ? `${m.nome} (${m.matricula})` : '';
      } } },
      legend: { display: false },
    },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { ticks: { font: { size: 11 } } } },
  };

  naoConfirmadasOptions: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    layout: { padding: { left: 16, right: 24, top: 8, bottom: 8 } },
    plugins: { tooltip: { enabled: true }, legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { ticks: { font: { size: 11 } } } },
  };

  recursosOptions: ChartConfiguration<'bar'>['options'] = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    layout: { padding: { left: 16, right: 24, top: 8, bottom: 8 } },
    plugins: { tooltip: { enabled: true }, legend: { position: 'top' } },
    scales: { x: { stacked: true, beginAtZero: true, ticks: { precision: 0 } }, y: { stacked: true, ticks: { font: { size: 11 } } } },
  };

  doughnutOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true, maintainAspectRatio: false,
    plugins: { tooltip: { enabled: true }, legend: { position: 'bottom' } },
  };

  groupedBarOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true, maintainAspectRatio: false,
    layout: { padding: { left: 8, right: 8, top: 8, bottom: 8 } },
    plugins: { tooltip: { enabled: true }, legend: { position: 'top' } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  pieOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: true, maintainAspectRatio: false,
    plugins: { tooltip: { enabled: true }, legend: { position: 'bottom' } },
  };

  constructor(private dashboardService: DashboardService) {
    const hoje = new Date();
    this.mesSelecionado = hoje.getMonth() + 1;
    this.anoSelecionado = hoje.getFullYear();
    for (let i = hoje.getFullYear(); i >= hoje.getFullYear() - 3; i--) this.anos.push(i);
  }

  ngOnInit(): void { this.carregarAba('concursos'); }

  carregarAba(tab: string): void {
    this.loading = true;
    const filtro: { dataInicio?: string; dataFim?: string; tab?: string } = { tab };
    if (this.periodoInicio && this.periodoFim) {
      filtro.dataInicio = this.formatDate(this.periodoInicio);
      filtro.dataFim = this.formatDate(this.periodoFim);
    }
    this.dashboardService.getDashboard(filtro).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.dashboard = { ...(this.dashboard || this.emptyDashboard()), ...this.mergeNonEmpty(data) };
        this.buildCharts(this.dashboard);
        this.loadedTabs.add(tab);
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
  }

  onTabChange(index: number): void {
    const tab = this.tabNames[index];
    if (!this.loadedTabs.has(tab)) {
      this.carregarAba(tab);
    }
  }

  onFiltroChange(): void {
    this.loadedTabs.delete('inscricoes');
    this.carregarAba('inscricoes');
  }

  onDateRangeChange(): void {
    if (this.periodoInicio && this.periodoFim) {
      this.loadedTabs.delete('inscricoes');
      this.carregarAba('inscricoes');
    }
  }

  limparPeriodo(): void {
    this.periodoInicio = null;
    this.periodoFim = null;
    this.loadedTabs.delete('inscricoes');
    this.carregarAba('inscricoes');
  }

  onTipoFiltroChange(): void {}

  private emptyDashboard(): DashboardData {
    return {
      concursosPorAndamento: {}, concursosInscricoesAbertas: [], concursosEncerradosPorUnidade: {},
      inscricoesPorStatus: {}, totalInscricoesPeriodo: null, concursosPorUnidade: {}, inscricoesPorUnidade: {},
      concursosPorTipo: {}, deferimentosPorTipo: {}, indeferimentosPorTipo: {}, concursosPorFase: {},
      inscricoesNaoConfirmadasPorConcurso: {}, concursosComissaoIncompleta: [], recursosPorConcurso: [],
      concursosPorMembro: [], provasPorFase: {}, inscricoesPorGeneroPorUnidade: [], inscricoesPorGeneroPorTipo: [],
      inscricoesPorStatusHeteroidentificacao: {}, provasPorTipoEFase: [], ausenciasPorTipoProva: {},
      fase1Aprovados: null, fase1Reprovados: null, fase2Aprovados: null, fase2Reprovados: null,
    };
  }
  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private mergeNonEmpty(data: DashboardData): Partial<DashboardData> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) continue;
      result[key] = value;
    }
    return result as Partial<DashboardData>;
  }

  private normalizeSexo(sexo: string): string {
    if (!sexo) return 'PREFIRO_NAO_INFORMAR';
    const upper = sexo.toUpperCase();
    if (upper === 'M' || upper === 'MASCULINO' || upper === 'HOMEM') return 'MASCULINO';
    if (upper === 'F' || upper === 'FEMININO' || upper === 'MULHER') return 'FEMININO';
    if (upper === 'PESSOA_TRANS') return 'PESSOA_TRANS';
    if (upper === 'NAO_BINARIO') return 'NAO_BINARIO';
    if (upper === 'OUTRO') return 'OUTRO';
    return 'PREFIRO_NAO_INFORMAR';
  }

  private buildCharts(data: DashboardData): void {
    // Detectar visão restrita
    const faseEntries = Object.entries(data.concursosPorFase || {});
    const ncEntries = Object.keys(data.inscricoesNaoConfirmadasPorConcurso || {});
    const recursosEntries = data.recursosPorConcurso || [];
    this.isVisaoRestrita = faseEntries.length > 0 || ncEntries.length > 0 || recursosEntries.length > 0;

    // Andamento
    const andLabelsFull = Object.keys(data.concursosPorAndamento || {});
    this.andamentoLabelsFull = andLabelsFull;
    this.andamentoHeight = Math.max(200, andLabelsFull.length * 35);
    this.chartAndamento = {
      labels: andLabelsFull.map(l => l.length > 15 ? l.substring(0, 15) + '...' : l),
      datasets: [{ data: Object.values(data.concursosPorAndamento || {}), backgroundColor: '#1976d2' }],
    };

    // Unidade
    const uniLabels = Object.keys(data.concursosPorUnidade || {});
    this.concursosPorUnidadeWidth = Math.max(400, uniLabels.length * 60);
    this.chartConcursosPorUnidade = { labels: uniLabels, datasets: [{ data: Object.values(data.concursosPorUnidade || {}), backgroundColor: '#00838f' }] };

    // Encerrados
    const encLabels = Object.keys(data.concursosEncerradosPorUnidade || {});
    this.encerradosPorUnidadeWidth = Math.max(400, encLabels.length * 60);
    this.chartEncerradosPorUnidade = { labels: encLabels, datasets: [{ data: Object.values(data.concursosEncerradosPorUnidade || {}), backgroundColor: '#5e35b1' }] };

    // Tipo
    const tipoNomes: Record<string, string> = { PT: 'Professor Doutor', PTPP: 'Professor Doutor - Pretos e Pardos' };
    const tipoLabels = Object.keys(data.concursosPorTipo || {}).map(k => tipoNomes[k] || k);
    this.chartConcursosPorTipo = { labels: tipoLabels, datasets: [{ data: Object.values(data.concursosPorTipo || {}), backgroundColor: ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f', '#4e342e', '#283593', '#558b2f', '#ad1457', '#00695c', '#bf360c', '#1565c0', '#6a1b9a'] }] };

    // Fase
    if (faseEntries.length > 0) {
      this.chartConcursosPorFase = { labels: faseEntries.map(([k]) => k === 'FASE1' ? 'Fase 1' : k === 'FASE2' ? 'Fase 2' : k), datasets: [{ data: faseEntries.map(([, v]) => v), backgroundColor: ['#1976d2', '#f57c00'] }] };
    }

    // Recursos
    const recursos = data.recursosPorConcurso || [];
    const processos = [...new Set(recursos.map(r => r.processo))];
    const pareceres = [...new Set(recursos.map(r => r.parecer))];
    const parecerColors: Record<string, string> = { deferido: '#4caf50', indeferido: '#f44336', 'em análise': '#ff9800' };
    this.recursosHeight = Math.max(200, processos.length * 35);
    this.chartRecursos = { labels: processos, datasets: pareceres.map(p => ({ label: p.charAt(0).toUpperCase() + p.slice(1), data: processos.map(proc => recursos.find(r => r.processo === proc && r.parecer === p)?.quantidade || 0), backgroundColor: parecerColors[p.toLowerCase()] || '#9e9e9e' })) };

    // Inscrições por status
    this.chartInscricoesPorStatus = { labels: Object.keys(data.inscricoesPorStatus || {}), datasets: [{ data: Object.values(data.inscricoesPorStatus || {}), backgroundColor: ['#4caf50', '#ff9800', '#f44336', '#9c27b0', '#2196f3'] }] };

    // Inscrições por unidade
    const insUniLabels = Object.keys(data.inscricoesPorUnidade || {});
    this.inscricoesPorUnidadeWidth = Math.max(400, insUniLabels.length * 60);
    this.chartInscricoesPorUnidade = { labels: insUniLabels, datasets: [{ data: Object.values(data.inscricoesPorUnidade || {}), backgroundColor: '#e65100' }] };

    // Não confirmadas
    const ncLabels = Object.keys(data.inscricoesNaoConfirmadasPorConcurso || {});
    this.naoConfirmadasHeight = Math.max(200, ncLabels.length * 35);
    this.chartNaoConfirmadas = { labels: ncLabels, datasets: [{ data: Object.values(data.inscricoesNaoConfirmadasPorConcurso || {}), backgroundColor: '#ff9800' }] };

    // Membros comissão
    const membros = data.concursosPorMembro || [];
    this.membrosLabelsFull = membros.map(m => ({ nome: m.nome, matricula: m.matricula }));
    this.membrosHeight = Math.max(200, membros.length * 30);
    this.chartMembros = { labels: membros.map(m => m.username), datasets: [{ data: membros.map(m => m.quantidadeConcursos), backgroundColor: '#1565c0' }] };

    // Provas por fase
    const provasEntries = Object.entries(data.provasPorFase || {});
    this.chartProvasPorFase = { labels: provasEntries.map(([k]) => k === 'FASE1' ? 'Fase 1' : k === 'FASE2' ? 'Fase 2' : k), datasets: [{ data: provasEntries.map(([, v]) => v), backgroundColor: ['#1976d2', '#f57c00'] }] };

    // Heteroidentificação
    const heteroEntries = Object.entries(data.inscricoesPorStatusHeteroidentificacao || {});
    this.chartHeteroidentificacao = {
      labels: heteroEntries.map(([k]) => k.replace(/_/g, ' ')),
      datasets: [{ data: heteroEntries.map(([, v]) => v), backgroundColor: '#00695c' }],
    };

    // Provas por tipo e fase (grouped bar: cada fase é um dataset, tipos são labels)
    const provasTF = data.provasPorTipoEFase || [];
    if (provasTF.length > 0) {
      const tipos = [...new Set(provasTF.map(p => p.sexo))]; // sexo = tipoProva (reuso do DTO)
      const fases = [...new Set(provasTF.map(p => p.categoria))]; // categoria = fase
      const faseColors: Record<string, string> = { FASE1: '#1976d2', FASE2: '#f57c00' };
      this.chartProvasPorTipoEFase = {
        labels: tipos.map(t => t.replace(/_/g, ' ')),
        datasets: fases.map(f => ({
          label: f === 'FASE1' ? 'Fase 1' : f === 'FASE2' ? 'Fase 2' : f,
          data: tipos.map(t => provasTF.find(p => p.categoria === f && p.sexo === t)?.quantidade || 0),
          backgroundColor: faseColors[f] || '#9e9e9e',
        })),
      };
    }

    // Ausências por tipo de prova
    const ausEntries = Object.entries(data.ausenciasPorTipoProva || {});
    this.chartAusencias = {
      labels: ausEntries.map(([k]) => k.replace(/_/g, ' ')),
      datasets: [{ data: ausEntries.map(([, v]) => v), backgroundColor: '#d32f2f' }],
    };

    // Resultado por fase (aprovados vs reprovados)
    if (data.fase1Aprovados != null || data.fase2Aprovados != null) {
      const labels: string[] = [];
      const aprovados: number[] = [];
      const reprovados: number[] = [];
      if (data.fase1Aprovados != null) {
        labels.push('Fase 1');
        aprovados.push(data.fase1Aprovados);
        reprovados.push(data.fase1Reprovados ?? 0);
      }
      if (data.fase2Aprovados != null) {
        labels.push('Fase 2');
        aprovados.push(data.fase2Aprovados);
        reprovados.push(data.fase2Reprovados ?? 0);
      }
      this.chartClassificacaoPorFase = {
        labels,
        datasets: [
          { label: 'Aprovados', data: aprovados, backgroundColor: '#4caf50' },
          { label: 'Reprovados', data: reprovados, backgroundColor: '#f44336' },
        ],
      };
    }

    // Gênero por unidade (grouped bar)
    const genUnidade = (data.inscricoesPorGeneroPorUnidade || []).map(g => ({ ...g, sexo: this.normalizeSexo(g.sexo) }));
    if (genUnidade.length > 0) {
      const categorias = [...new Set(genUnidade.map(g => g.categoria))];
      const sexos = [...new Set(genUnidade.map(g => g.sexo))];
      const sexoColors: Record<string, string> = { FEMININO: '#e91e63', MASCULINO: '#1976d2', PESSOA_TRANS: '#9c27b0', NAO_BINARIO: '#ff9800', OUTRO: '#607d8b', PREFIRO_NAO_INFORMAR: '#9e9e9e' };
      const sexoLabels: Record<string, string> = { FEMININO: 'Feminino', MASCULINO: 'Masculino', PESSOA_TRANS: 'Pessoa Trans', NAO_BINARIO: 'Não Binário', OUTRO: 'Outro', PREFIRO_NAO_INFORMAR: 'Não informado' };
      this.generoPorUnidadeWidth = Math.max(400, categorias.length * 80);
      this.chartGeneroPorUnidade = {
        labels: categorias,
        datasets: sexos.map(s => ({
          label: sexoLabels[s] || s,
          data: categorias.map(cat => genUnidade.filter(g => g.categoria === cat && g.sexo === s).reduce((sum, g) => sum + g.quantidade, 0)),
          backgroundColor: sexoColors[s] || '#9e9e9e',
        })),
      };
    }

    // Gênero por tipo de concurso (grouped bar)
    const genTipo = (data.inscricoesPorGeneroPorTipo || []).map(g => ({ ...g, sexo: this.normalizeSexo(g.sexo) }));
    if (genTipo.length > 0) {
      const categorias = [...new Set(genTipo.map(g => g.categoria))];
      const sexos = [...new Set(genTipo.map(g => g.sexo))];
      const sexoColors: Record<string, string> = { FEMININO: '#e91e63', MASCULINO: '#1976d2', PESSOA_TRANS: '#9c27b0', NAO_BINARIO: '#ff9800', OUTRO: '#607d8b', PREFIRO_NAO_INFORMAR: '#9e9e9e' };
      const sexoLabels: Record<string, string> = { FEMININO: 'Feminino', MASCULINO: 'Masculino', PESSOA_TRANS: 'Pessoa Trans', NAO_BINARIO: 'Não Binário', OUTRO: 'Outro', PREFIRO_NAO_INFORMAR: 'Não informado' };
      this.chartGeneroPorTipo = {
        labels: categorias,
        datasets: sexos.map(s => ({
          label: sexoLabels[s] || s,
          data: categorias.map(cat => genTipo.filter(g => g.categoria === cat && g.sexo === s).reduce((sum, g) => sum + g.quantidade, 0)),
          backgroundColor: sexoColors[s] || '#9e9e9e',
        })),
      };
    }
  }
}
